import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { HardHat, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/auth';

/**
 * Kompakt taşeron ekleme modalı — sadece "Ad Soyad" + "Plaka" + opsiyonel
 * "Not". Kayıt başarılıysa parent'ı `onCreated(sub)` ile bilgilendirip kapanır.
 *
 * Plaka boş bırakılabilir (bazı taşeronlar fix bir araca bağlı değilse) ama
 * UX gereği uyarı veriyoruz çünkü atamada plaka yoksa kart anonim görünür.
 */
export function SubcontractorDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    const fn = name.trim();
    if (!fn) {
      toast.error('Ad soyad gerekli');
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post('/planning/subcontractors', {
        full_name: fn,
        vehicle_code: plate.trim().toUpperCase() || undefined,
        note: note.trim() || undefined,
      });
      const sub = res.data?.data;
      toast.success(`${sub.full_name} eklendi`);
      setName(''); setPlate(''); setNote('');
      onCreated?.(sub);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eklenemedi');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="planning-subc-dialog-overlay"
    >
      <Card
        className="max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="planning-subc-dialog"
      >
        <div className="flex items-center gap-2 mb-1">
          <HardHat className="h-5 w-5 text-amber-500" />
          <h3 className="text-base font-semibold">Taşeron Ekle</h3>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-slate-400 hover:text-slate-700"
            data-testid="planning-subc-dialog-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Geçici taşeron şoför + plaka kaydı. İleride bu kayıtları "alt firma"
          olarak kalıcı hale getirebilirsiniz.
        </p>
        <div className="space-y-2.5">
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Ad Soyad <span className="text-rose-500">*</span></label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="örn. Mehmet Yılmaz"
              autoFocus
              data-testid="planning-subc-name"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Plaka</label>
            <Input
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="örn. 1ABC234"
              className="font-mono uppercase"
              data-testid="planning-subc-plate"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Not (opsiyonel)</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="örn. DPD haftalık taşeron"
              data-testid="planning-subc-note"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4 justify-end">
          <Button variant="ghost" onClick={onClose} data-testid="planning-subc-cancel">Vazgeç</Button>
          <Button onClick={handleSave} disabled={busy || !name.trim()} data-testid="planning-subc-save">
            {busy ? 'Ekleniyor…' : 'Ekle'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
